import React, { useRef, useState, useEffect } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";

import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Popover from "@mui/material/Popover";
import clsx from "clsx";
import Draggable from "react-draggable";

import { HiDotsVertical } from "react-icons/hi";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

const StyledTableWrapper = styled("div")({
  // Match the legacy react-virtualized table look: dark header row, white text,
  // and inset black box-shadow on each cell so we get visible 1px borders that
  // collapse cleanly between sibling cells.
  boxShadow:
    "0 1px black inset, 0 -1px black inset, -1px 0 black inset, 1px 0 black inset",
  ".vt-flex-container": {
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  ".vt-header-row": {
    backgroundColor: "#303030",
    color: "#fff",
    boxShadow:
      "0 1px black inset, 0 -1px black inset, -1px 0 black inset, 1px 0 black inset",
  },
  ".vt-table-cell": {
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    flex: 1,
    padding: "0 4px",
    boxShadow:
      "0 1px black inset, 0 -1px black inset, -1px 0 black inset, 1px 0 black inset",
  },
  ".vt-table-cell-hover": {
    cursor: "pointer",
  },
  ".vt-table-cell-hover:hover": {
    backgroundColor: "#ffcc1b",
  },
  ".vt-table-row-hover": {
    cursor: "pointer",
  },
  ".vt-table-row-hover:hover": {
    backgroundColor: "#ffcc1b",
    color: "#000",
  },
});

const Header = ({
  label,
  columnIndex,
  dataKey,
  handleHeaderClick,
  columns,
  handleResize,
  handleDragStop,
  widths,
  columnReorder,
  handleHeaderAuxClick,
  headerStyle,
}) => {
  const dragHandleRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: dataKey, disabled: !columnReorder });

  const style = {
    ...headerStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: transform ? "grabbing" : columnReorder ? "grab" : undefined,
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const canHandleHeaderClick = handleHeaderClick ? true : false;
  return (
    <React.Fragment key={dataKey}>
      <span
        title={label}
        id={"VirtualizedTableCol" + columnIndex}
        key={dataKey}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="ReactVirtualized__Table__headerTruncatedText"
        onClick={(ev) => {
          if (canHandleHeaderClick) handleHeaderClick(ev, label, columnIndex);
        }}
        onContextMenu={(ev) => {
          ev.preventDefault();
          handleHeaderAuxClick(ev, columnIndex);
        }}
      >
        {label}
      </span>
      {columnIndex !== columns.length - 1 ? (
        <Draggable
          nodeRef={dragHandleRef}
          axis="x"
          onDrag={(event, { deltaX }) => handleResize(dataKey, deltaX)}
          onStop={() => handleDragStop(widths)}
          position={{ x: 0 }}
          zIndex={999}
          defaultClassName="DragHandle"
          defaultClassNameDragging="DragHandleActive"
        >
          <div ref={dragHandleRef} className="DragHandleIcon">
            <HiDotsVertical />
          </div>
        </Draggable>
      ) : null}
    </React.Fragment>
  );
};

const HeaderRenderer = (props) => {
  return (
    <SortableContext
      id={props.dataKey}
      items={props.columns.map((x) => x.dataKey)}
      strategy={horizontalListSortingStrategy}
    >
      <Header {...props} />
    </SortableContext>
  );
};

const DEFAULT_MIN_WIDTH_CELL = 20;
const DEFAULT_MAX_WIDTH_CELL = 600;

const getCellValue = (rowData, dataKey) => {
  if (!rowData || !dataKey) return undefined;
  if (!dataKey.includes(".")) return rowData[dataKey];
  return dataKey.split(".").reduce((acc, key) => acc?.[key], rowData);
};

const defaultCellRenderer = ({ cellData, columnIndex, columns, rowHeight }) => {
  const isNumeric =
    columnIndex != null && columns[columnIndex].numeric;
  return (
    <div
      className={clsx("vt-table-cell", "vt-table-cell-hover")}
      style={{
        height: rowHeight,
        justifyContent: isNumeric ? "flex-end" : "center",
      }}
    >
      {cellData}
    </div>
  );
};

const Row = ({ index, style, data }) => {
  const {
    columns,
    widths,
    width,
    rowHeight,
    rowGetter,
    rowData: allRows,
    handleRowClick,
    cellRenderer,
    rowClassName,
  } = data;

  const rowData = rowGetter ? rowGetter({ index }) : allRows?.[index];

  const handleRowKeyDown = handleRowClick
    ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleRowClick({ index, rowData, event });
        }
      }
    : undefined;

  return (
    <div
      style={style}
      className={rowClassName(index)}
      role="row"
      aria-rowindex={index + 1}
      tabIndex={handleRowClick ? 0 : undefined}
      onClick={
        handleRowClick
          ? (event) => handleRowClick({ index, rowData, event })
          : undefined
      }
      onKeyDown={handleRowKeyDown}
    >
      {columns.map(({ dataKey, label, ...other }, columnIndex) => {
        const cellData = getCellValue(rowData, dataKey);
        const renderCell = cellRenderer
          ? cellRenderer({
              cellData,
              columnIndex,
              rowData,
              dataKey,
              rowIndex: index,
              columnData: other,
            })
          : defaultCellRenderer({
              cellData,
              columnIndex,
              columns,
              rowHeight,
            });

        return (
          <div
            key={`${dataKey}-${index}`}
            className={clsx("vt-table-cell", "vt-table-cell-hover")}
            style={{
              width: widths[dataKey] * width,
              height: rowHeight,
              overflow: "hidden",
            }}
            title={
              typeof cellData === "string" || typeof cellData === "number"
                ? `${cellData}`
                : undefined
            }
          >
            {renderCell}
          </div>
        );
      })}
    </div>
  );
};

function VirtualizedTable(props) {
  const {
    rowHeight = 48,
    enableHeader,
    rowHover,
    headerHeight = 48,
    width,
    height,
    ...tableProps
  } = props;
  const [columns, setColumns] = useState([]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const clickedHeader = useRef();
  const [anchorEl, setAnchorEl] = useState();
  const [widths, setWidths] = useState({});

  useEffect(() => {
    setColumns(props.columns);
    setWidths(
      props.columns.reduce((acc, cur) => {
        acc[cur.dataKey] = 100 / props.columns.length / 100;
        return acc;
      }, {})
    );
  }, [props.columns]);

  const handleDragStop = () => {};

  const handleResize = (_width) => (dataKey, deltaX) => {
    setWidths((prevState) => {
      const prevWidths = prevState;
      const percentDelta = deltaX / _width;

      const nextDataKey =
        columns[columns.findIndex((x) => x.dataKey === dataKey) + 1].dataKey;

      const minWidth = DEFAULT_MIN_WIDTH_CELL;
      const maxWidth = DEFAULT_MAX_WIDTH_CELL;

      const newPercentDelta = prevWidths[dataKey] + percentDelta;
      const newNextPercentDelta = prevWidths[nextDataKey] - percentDelta;

      const newWidth = newPercentDelta * _width;
      const newNextWidth = newNextPercentDelta * _width;

      if (
        newWidth >= minWidth &&
        newWidth <= maxWidth &&
        newNextWidth >= minWidth &&
        newNextWidth <= maxWidth
      ) {
        return {
          ...prevWidths,
          [dataKey]: newPercentDelta,
          [nextDataKey]: newNextPercentDelta,
        };
      } else {
        return prevState;
      }
    });
  };

  const getRowClassName = (rowIndex) =>
    clsx("vt-flex-container", {
      "vt-table-row-hover": rowHover ? rowIndex !== -1 : false,
    });

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setColumns((_columns) => {
        const oldIndex = _columns.findIndex((x) => x.dataKey === active.id);
        const newIndex = _columns.findIndex((x) => x.dataKey === over.id);
        const newColumns = arrayMove(_columns, oldIndex, newIndex);
        props.onColumnChange && props.onColumnChange(newColumns);
        return newColumns;
      });
    }
  }

  const handleHeaderAuxClick = (ev, columnIndex) => {
    if (ev.button === 2) {
      clickedHeader.current = columnIndex;
      setAnchorEl(ev.target);
    }
  };

  const closePopovers = () => {
    setAnchorEl(null);
  };

  const handleHeaderOptionClick = (dataKey) => (ev) => {
    setColumns((_columns) => {
      // TODO handle new column
      const oldIndex = _columns.findIndex((x) => x.dataKey === dataKey);
      const newIndex = clickedHeader.current;
      const newColumns = arrayMove(_columns, oldIndex, newIndex);
      props.onColumnChange && props.onColumnChange(newColumns);
      return newColumns;
    });
    closePopovers();
  };

  return (
    <StyledTableWrapper role="grid" aria-rowcount={tableProps.rowCount || 0}>
      <AutoSizer disableHeight={!!height} disableWidth={!!width}>
        {(dimensions) => {
          const _width = width || dimensions.width;
          const _height = height || dimensions.height;
          const tableBodyHeight = Math.max(
            0,
            _height - (enableHeader ? headerHeight : 0)
          );

          const rowItemData = {
            columns,
            widths,
            width: _width,
            rowHeight,
            rowGetter: tableProps.rowGetter,
            rowData: tableProps.rowData,
            handleRowClick: props.handleRowClick,
            cellRenderer: props.cellRenderer,
            rowClassName: getRowClassName,
          };

          return (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              {enableHeader && (
                <div
                  className="vt-flex-container vt-header-row"
                  style={{ height: headerHeight, width: _width }}
                >
                  {columns.map(({ dataKey, label }, index) => (
                    <div
                      key={dataKey}
                      className="vt-table-cell"
                      style={{
                        width: widths[dataKey] * _width,
                        overflow: "hidden",
                        // Use flex so the label shrinks and the trailing drag
                        // handle stays inside the cell (otherwise the
                        // overflow:hidden clips the handle when the label is
                        // wider than the column).
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        minWidth: 0,
                      }}
                    >
                      <HeaderRenderer
                        dataKey={dataKey}
                        label={label}
                        headerStyle={props.headerStyle}
                        columnIndex={index}
                        handleHeaderClick={props.handleHeaderClick}
                        columns={columns}
                        handleResize={handleResize(_width)}
                        handleDragStop={handleDragStop}
                        widths={widths}
                        columnReorder={props.columnReorder}
                        handleHeaderAuxClick={handleHeaderAuxClick}
                      />
                    </div>
                  ))}
                </div>
              )}

              <FixedSizeList
                height={tableBodyHeight}
                width={_width}
                itemCount={tableProps.rowCount || 0}
                itemSize={rowHeight}
                itemData={rowItemData}
              >
                {Row}
              </FixedSizeList>
            </DndContext>
          );
        }}
      </AutoSizer>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closePopovers}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <div className="navigation-content-body nav-menu">
          <Stack spacing={1}>
            {columns.map((column, i) => (
              <button
                key={column.dataKey}
                type="button"
                className="navigation-item"
                onClick={handleHeaderOptionClick(column.dataKey)}
              >
                {column.label}
              </button>
            ))}
          </Stack>
        </div>
      </Popover>
    </StyledTableWrapper>
  );
}

export default VirtualizedTable;
